import isEmpty from 'lodash.isempty';
import prepareSerializedStaticDocument from '../../hdrm/utils/helpers/prepare-serialized-static-document';
import getCurrentLocale from '../../site-locales/selectors/current-locale';
import getPageLocaleById from '../../site-locales/selectors/page-locale-by-id';
import site from '../../site';
import template from '../../template';
import hdrm from '../../hdrm';
import getGlobal from '../selectors/global';
import saveGlobals from '../helpers/save-globals';
import getChangedSettingsAccordingToGlobalContent from '../helpers/get-changed-settings-according-to-global-content';
import constants from '../constants';

const { globalIds } = constants;

const collectGlobalsToUpdate = ({
  state,
  settings,
  variables,
  socialIcons,
  updateCopyrightGlobal = false,
  options,
}) => {
  const globalsToUpdate = [];

  Object.keys(settings).forEach((setting) => {
    if (!variables[setting]) return;
    const settingValue = settings[setting];

    variables[setting].forEach((variable) => {
      const global = getGlobal(state, variable.globalId);
      if (!global || !global.html || (global.id === globalIds.COPYRIGHT && !updateCopyrightGlobal))
        return;

      const { html, id } = global;
      const newHTML = site.helpers.injectSiteSettings({
        variable,
        settingValue,
        setting,
        html,
        socialIcons,
        options,
      });

      globalsToUpdate.push({ id, newHTML });
    });
  });

  return globalsToUpdate;
};

const applyGlobalsAndUpdatePage = ({ globalsToUpdate, activePageId, activePage, store }) => {
  saveGlobals(globalsToUpdate);

  const staticDOM = hdrm.instance.driver.getStaticDocument();
  if (activePageId && activePage?.html && staticDOM) {
    const newHTML = prepareSerializedStaticDocument(staticDOM);
    store.dispatch(site.actions.setPageHTML(activePageId, newHTML));
  }
};

const updateGlobalHtmlBySettings = (store) => (next) => (action) => {
  switch (action.type) {
    case site.actionTypes.UPDATE_SETTINGS: {
      const state = store.getState();
      const variables = template.selectors.getVariables(state);
      const socialIcons = template.selectors.getSocialIcons(state);
      const activePageId = site.selectors.getActivePageId(state);
      const activePage = site.selectors.getPage(state, activePageId);
      const { settings, updateGlobals, updateCopyrightGlobal, options } = action.payload;

      if (!updateGlobals || !settings || isEmpty(variables)) {
        next(action);
        return;
      }

      const globalsToUpdate = collectGlobalsToUpdate({
        state,
        settings,
        variables,
        socialIcons,
        updateCopyrightGlobal,
        options,
      });

      applyGlobalsAndUpdatePage({ globalsToUpdate, activePageId, activePage, store });

      next(action);
      break;
    }

    /*
     * This was added to fix an issue with incorrect contact information on globals after changing the page locale
     * Ticket link:
     * https://github.com/yola/production/issues/18232
     */
    case site.actionTypes.SET_ACTIVE_PAGE: {
      const state = store.getState();
      const { pageId } = action.payload;

      const variables = template.selectors.getVariables(state);
      const socialIcons = template.selectors.getSocialIcons(state);
      const activePageId = site.selectors.getActivePageId(state);
      const activePage = site.selectors.getPage(state, activePageId);
      const settings = site.selectors.getSettings(state);

      const activePageLocale = getCurrentLocale(state);
      const nextActivePageLocale = getPageLocaleById(state, pageId);

      const shouldSkip = !pageId || isEmpty(variables) || activePageLocale === nextActivePageLocale;

      if (shouldSkip) {
        next(action);
        return;
      }

      const changedSettings = getChangedSettingsAccordingToGlobalContent(settings);

      if (isEmpty(changedSettings)) {
        next(action);
        return;
      }

      const globalsToUpdate = collectGlobalsToUpdate({
        state,
        settings: changedSettings,
        variables,
        socialIcons,
      });

      applyGlobalsAndUpdatePage({ globalsToUpdate, activePageId, activePage, store });

      next(action);
      break;
    }

    default:
      next(action);
  }
};

export default updateGlobalHtmlBySettings;
